// src/contexts/SettingsContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AppSettings } from '../types';
import { apiRequest } from '../lib/api';

interface SettingsContextValue {
  settings: AppSettings;
  loading: boolean;
  refreshSettings: () => Promise<void>;
  updateSettings: (newSettings: Partial<AppSettings>) => Promise<void>;
}

const defaultSettings: AppSettings = {
  app_name: 'Geobit Portal',
  company_name: 'Geobit Group',
  timezone: 'Asia/Dubai',
  date_format: 'YYYY-MM-DD HH:mm',
  default_page_size: '25',
  session_duration_hours: '12',
  min_margin_threshold_pct: '15.0',
  min_margin_enforcement_mode: 'warning',
  min_margin_cost_basis: 'net_cost',
};

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);

  const refreshSettings = useCallback(async () => {
    try {
      const data = await apiRequest<AppSettings>('/api/settings');
      if (data) {
        setSettings(data);
        if (data.app_name) {
          document.title = data.app_name;
        }
      }
    } catch (err) {
      console.warn('Could not load settings, using defaults.', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  const updateSettings = async (newSettings: Partial<AppSettings>) => {
    const updated = await apiRequest<{ message: string; settings: AppSettings }>('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(newSettings),
    });
    setSettings(prev => ({ ...prev, ...newSettings }));
    if (newSettings.app_name) {
      document.title = newSettings.app_name;
    }
  };

  return (
    <SettingsContext.Provider value={{ settings, loading, refreshSettings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextValue => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
